import React, { FC, useMemo, useState } from 'react';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import styles from './CancelRevisionSigningModal.module.scss';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import {
  MultipleSelector,
  MultipleSelectorOption,
} from 'components/uikit/selector/MultipleSelector.tsx';
import { QueryFactory } from 'services/api/index.ts';
import { CancelRevisionSigningConfirmationModal } from './CancelRevisionSigningConfirmationModal.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { Status } from 'services/api/api-client.types.ts';
import { useSignatureBlockQueriesInvalidation } from 'pages/authorized/documents/documentList/signatureBlock/useSignatureBlockQueriesInvalidation.tsx';
import { Loading } from 'components/uikit/suspense/Loading.tsx';

export type StopDocumentSigningModalProps = {
  projectId: number;
  revisionId: number;
  isOpen: boolean;
  onClose: () => void;
};

export const CancelRevisionSigningModal: FC<StopDocumentSigningModalProps> = (
  props,
) => {
  const i18n = useScopedTranslation('Component.CancelRevisionSigningModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const [selectedOptions, setSelectedOptions] = useState<
    MultipleSelectorOption[]
  >([]);
  const documentsQuery = QueryFactory.DocumentQuery.useSearchQuery(
    {
      revisionId: props.revisionId,
      status: Status.Signing,
    },
    { enabled: !!props.revisionId && props.isOpen },
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const onClose = () => {
    props.onClose();
  };
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation({
    revisionId: props.revisionId,
  });

  const options = useMemo<MultipleSelectorOption[]>(() => {
    return (
      documentsQuery.data?.data.map((document) => {
        return { id: document.id, label: document.name };
      }) ?? []
    );
  }, [documentsQuery.data]);

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onClose}
      isBlocking={true}
      title={i18n.t('title')}
    >
      <Loading loading={documentsQuery.isLoading}>
        <div className={styles.message}>
          <p>
            {options.length > 0
              ? i18n.t('message_with_documents')
              : i18n.t('message_without_documents')}
          </p>

          {options.length > 0 && (
            <div className={styles.selector}>
              <MultipleSelector
                showSelectAll
                selectAllOnMount
                onSelectionChange={setSelectedOptions}
                options={options}
              />
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <Button
            title={buttonTitle.t('cancel')}
            color={ButtonColor.Secondary}
            className={styles.button}
            onClick={onClose}
          />
          <Button
            testId={'delete-button'}
            title={buttonTitle.t('confirm')}
            color={ButtonColor.DestroyPrimary}
            className={styles.button}
            disabled={options.length !== 0 && selectedOptions.length === 0}
            onClick={async () => {
              if (options.length === 0) {
                await QueryFactory.DocumentSigningClient.stopRevisionSigning(
                  props.revisionId,
                );
                await invalidateSignatureBlockQueries();
                onClose();
              } else {
                setShowConfirm(true);
              }
            }}
          />
        </div>
      </Loading>
      <CancelRevisionSigningConfirmationModal
        projectId={props.projectId}
        revisionId={props.revisionId}
        options={selectedOptions}
        isOpen={showConfirm}
        onClose={(closeParent) => {
          if (closeParent) onClose();
          setShowConfirm(false);
        }}
      />
    </CustomModal>
  );
};
